import { useEffect } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { NotificationContextProvider, useNotification } from 'app/notifications';
import { SectionTestLogs, StudentApi, TestSectionDetail } from 'apis/StudentApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { DefaultLayout } from 'components/layout';
import { getFormattedDate } from 'utils';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/grading/edit_section_test/EditSectionTest.scss';

const formatSectionDates = (sections: TestSectionDetail[]): TestSectionDetail[] => sections.map(section => ({
    ...section,
    LockoutDate: section.LockoutDate !== '' ? getFormattedDate(new Date(section.LockoutDate)) : section.LockoutDate,
    StartDate: section.StartDate !== '' ? getFormattedDate(new Date(section.StartDate)) : section.StartDate
}));

const EditSectionTestInner = () => {

    const [params] = useSearchParams();
    const studentId = Number(params.get('studentId'));
    const resultId = Number(params.get('resultId'));
    const { cultureId } = useApp();
    const { formatMessage } = useIntl();
    const { onErrorToast, onSuccessToast } = useNotification();

    const [state, setState] = useSuperState({
        cascadeUpdate: true,
        comment: '',
        commentValid: true,
        currentSection: undefined as TestSectionDetail | undefined,
        noChanges: true,
        originalLockoutDate: '',
        originalStartDate: '',
        sections: [] as TestSectionDetail[]
    });
    const { cascadeUpdate, comment, commentValid, currentSection, noChanges, originalLockoutDate, originalStartDate, sections } = state;

    const activeId = currentSection?.Id;

    const loadSections = () => {

        StudentApi.GetSectionTestDetails(studentId, resultId, cultureId)
            .then(result => setState({ sections: formatSectionDates(result) }))
            .catch(console.error);

    };

    useEffect(() => {

        if (!studentId || !resultId) return;
        loadSections();

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!studentId || !resultId) return null;

    const editPropertyValue = (property: 'StartDate' | 'LockoutDate', value: any) => {

        if (!currentSection) return;

        const formatted = getFormattedDate(new Date(value));
        const newCurrentSection = { ...currentSection, [property]: formatted };
        const isDirty =
            newCurrentSection.StartDate !== originalStartDate ||
            newCurrentSection.LockoutDate !== originalLockoutDate;

        setState({
            currentSection: newCurrentSection,
            noChanges: !isDirty
        });

    };

    const handleSuccessCall = (savedSection: TestSectionDetail) => {

        StudentApi.GetSectionTestDetails(studentId, resultId, cultureId)
            .then(result => {

                const refreshed = formatSectionDates(result);
                const refreshedCurrent = refreshed.find(s => s.Id === savedSection.Id);

                setState({
                    comment: '',
                    commentValid: true,
                    currentSection: refreshedCurrent ?? savedSection,
                    noChanges: true,
                    originalLockoutDate: (refreshedCurrent ?? savedSection).LockoutDate,
                    originalStartDate: (refreshedCurrent ?? savedSection).StartDate,
                    sections: refreshed
                });

            })
            .catch(console.error);

        onSuccessToast(formatMessage({ id: 'successfully_updated_name.623ccbd8', defaultMessage: 'Successfully updated {name}.' }, { name: savedSection.SectionName }));

    };

    const onSaveEditSectionClick = () => {

        const isCommentValid = comment.length > 0;

        if (isCommentValid && currentSection) {

            StudentApi.UpdateSectionTestDetails(
                comment,
                resultId,
                currentSection.SectionTestResultId,
                currentSection.SectionResultId,
                currentSection.StartDate,
                currentSection.LockoutDate,
                cascadeUpdate
            )
                .then(response => {

                    if (response.Done) {

                        handleSuccessCall(currentSection);

                    } else {

                        onErrorToast(`${formatMessage({ id: 'failed_to_update.419f8ec3', defaultMessage: 'Failed to update' })} ${currentSection.SectionName}.`);

                    }

                })
                .catch(console.error);

        }

        setState({ commentValid: isCommentValid });

    };

    const toggleEditCloseButtonClick = (sectionDetail: TestSectionDetail) => {

        const isClosing = activeId === sectionDetail.Id;

        setState({
            comment: '',
            commentValid: true,
            currentSection: isClosing ? undefined : sectionDetail,
            noChanges: true,
            originalLockoutDate: isClosing ? '' : sectionDetail.LockoutDate,
            originalStartDate: isClosing ? '' : sectionDetail.StartDate
        });

    };

    return (
        <Container>
            {sections.length > 0 && (
                <SectionList
                    activeId={activeId}
                    cascadeUpdate={cascadeUpdate}
                    onCascadeChange={checked => setState({ cascadeUpdate: checked })}
                    onToggleEdit={toggleEditCloseButtonClick}
                    sections={sections}
                />
            )}
            {currentSection && (
                <SectionEditor
                    comment={comment}
                    commentValid={commentValid}
                    currentSection={currentSection}
                    noChanges={noChanges}
                    onCommentChange={value => setState({ comment: value })}
                    onDateChange={editPropertyValue}
                    onSave={onSaveEditSectionClick}
                />
            )}
        </Container>
    );

};

interface SectionListProps {
    activeId: number | undefined;
    cascadeUpdate: boolean;
    onCascadeChange: (checked: boolean) => void;
    onToggleEdit: (section: TestSectionDetail) => void;
    sections: TestSectionDetail[];
}

const SectionList = ({ activeId, cascadeUpdate, onCascadeChange, onToggleEdit, sections }: SectionListProps) => {

    const { formatMessage } = useIntl();

    return (
        <Row className="px-4 border-bottom pb-4 pt-2 my-3 edit-section-container">
            <Col xl={12}>
                <h2>
                    {sections[0].TestName}
                </h2>
            </Col>
            <Col className="py-2" xl={12}>
                <GMetrixCheckInput
                    checked={cascadeUpdate}
                    label={formatMessage({ id: 'apply_changes_to_all_unfinished_sections.960b22d6', defaultMessage: 'Apply Changes to All Unfinished Sections' })}
                    onChange={e => onCascadeChange(e.target.checked)}
                    type={'checkbox'}
                />
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixInput
                    readOnly
                    label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                    value={sections[0].ParentStartDate}
                />
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixInput
                    readOnly
                    label={formatMessage({ id: 'due_date.712b37be', defaultMessage: 'Due Date' })}
                    value={sections[0].ParentDueDate}
                />
            </Col>
            {sections.map(sectionDetail => (
                <Col key={sectionDetail.Id} xl={12}>
                    <div className="px-2 d-flex align-items-center border justify-content-between my-2 edit-section-container__list-item">
                        <div>
                            {sectionDetail.SectionName}
                        </div>
                        <GMetrixButton
                            className="button-min"
                            onClick={() => onToggleEdit(sectionDetail)}
                            variant="primary"
                        >
                            {activeId === sectionDetail.Id ? <FormattedMessage defaultMessage="Close" id="close.99e01fb5" /> : <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />}
                        </GMetrixButton>
                    </div>
                </Col>
            ))}
        </Row>
    );

};

interface SectionEditorProps {
    comment: string;
    commentValid: boolean;
    currentSection: TestSectionDetail;
    noChanges: boolean;
    onCommentChange: (value: string) => void;
    onDateChange: (property: 'StartDate' | 'LockoutDate', value: any) => void;
    onSave: () => void;
}

const SectionEditor = ({ comment, commentValid, currentSection, noChanges, onCommentChange, onDateChange, onSave }: SectionEditorProps) => {

    const { formatMessage } = useIntl();

    const startDateValue = !currentSection.StartDate ? undefined : new Date(currentSection.StartDate);
    const lockoutDateValue = !currentSection.LockoutDate ? undefined : new Date(currentSection.LockoutDate);
    const isEditable = currentSection.StatusId < 3;

    return (
        <Row className="px-4 border-bottom pb-4 pt-2 my-3 edit-section-container">
            <Col className="py-1" xl={12}>
                <h2>
                    {currentSection.SectionName}
                </h2>
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixInput
                    readOnly
                    label={formatMessage({ id: 'number_of_questions.b2978bc0', defaultMessage: 'Number of Questions' })}
                    value={currentSection.NumQuestions}
                />
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixInput readOnly label={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })} value={currentSection.Status} />
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixDatePicker
                    label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                    onChange={(date: any) => onDateChange('StartDate', date)}
                    value={startDateValue}
                />
            </Col>
            <Col className="py-1" xl={6}>
                <GMetrixDatePicker
                    label={formatMessage({ id: 'lockout_date.8902de7b', defaultMessage: 'Lockout Date' })}
                    onChange={(date: any) => onDateChange('LockoutDate', date)}
                    value={lockoutDateValue}
                />
            </Col>
            <Col className="py-1" xl={isEditable ? 6 : 12}>
                <Form.Label className="edit-section-container__text-editor">
                    <FormattedMessage defaultMessage="Description" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="description.6317db73" />
                </Form.Label>
                <div
                    className="edit-section-container__description"
                    dangerouslySetInnerHTML={{ __html: currentSection.Description }}
                />
            </Col>
            {isEditable && (
                <>
                    <Col className="py-1" xl={6}>
                        <GMetrixInput
                            required
                            label={
                                commentValid
                                    ? formatMessage({ id: 'comment.252dd7a3', defaultMessage: 'Comment', description: 'Formulario' })
                                    : formatMessage({ id: 'comment_please_fill_out_this_field.61d466fb', defaultMessage: 'Comment - Please fill out this field' })
                            }
                            onChange={e => onCommentChange(e.target.value.toString())}
                            type="textarea"
                            value={comment}
                        />
                    </Col>
                    <Col className="d-flex justify-content-end mt-2" xl={12}>
                        <GMetrixButton
                            className="button-min"
                            disabled={noChanges}
                            onClick={onSave}
                            variant="success"
                        >
                            <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                        </GMetrixButton>
                    </Col>
                </>
            )}
            {currentSection.SectionTestLogs.length > 0 && (
                <SectionLogList logs={currentSection.SectionTestLogs} />
            )}
        </Row>
    );

};

interface SectionLogListProps {
    logs: SectionTestLogs[];
}

const SectionLogList = ({ logs }: SectionLogListProps) => (
    <>
        <Col className="my-3 border-top pt-2" md={12} sm={12} xl={12} xs={12}>
            <h2>
                <FormattedMessage defaultMessage="Recent History" id="recent_history.44e5c6a1" />
            </h2>
        </Col>
        <Col className="log-scroll-wrapper" md={12} sm={12} xl={12} xs={12}>
            {logs.map((sectionTestLog, i) => (
                <Row className="p-2 border log-container my-2" key={i}>
                    <Col lg={6} md={6} sm={12} xl={6} xs={12}>
                        <div className="log-title">
                            <FormattedMessage defaultMessage="Modified By" id="modified_by.99d0d9af" />
                            :
                            {' '}
                        </div>
                        <div>
                            {sectionTestLog.UserThatUpdated}
                        </div>
                    </Col>
                    <Col lg={6} md={6} sm={12} xl={6} xs={12}>
                        <div className="log-title">
                            <FormattedMessage defaultMessage="Changes Made" id="changes_made.0d00ba12" />
                            :
                            {' '}
                        </div>
                        <div>
                            {new Date(sectionTestLog.UpdatedAt).toLocaleDateString()}
                        </div>
                    </Col>
                    <Col lg={12} md={12} sm={12} xl={12} xs={12}>
                        <div className="log-title">
                            <FormattedMessage defaultMessage="Comment" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="comment.e2d25fe4" />
                            :
                        </div>
                        <div>
                            {sectionTestLog.Comment}
                        </div>
                    </Col>
                </Row>
            ))}
        </Col>
    </>
);

export const EditSectionTest = () => (
    <DefaultLayout>
        <NotificationContextProvider>
            <EditSectionTestInner />
        </NotificationContextProvider>
    </DefaultLayout>
);
